/**
 * VH-221: how a stored blood test value reads.
 *
 * A censored lab result ("> 52.0") is stored as its limit plus an operator.
 * It must read "above 52.0" — never the bare limit, and never 0.
 * Qualitative results keep a 0.0 placeholder, so their text is shown instead.
 */

const OPERATOR_KEYS = {
  '>': 'bloodTests.context.value.above',
  '>=': 'bloodTests.context.value.atOrAbove',
  '<': 'bloodTests.context.value.below',
  '<=': 'bloodTests.context.value.atOrBelow',
}

export function isCensored(result) {
  return result?.value_kind === 'censored' && Boolean(OPERATOR_KEYS[result?.value_operator])
}

/** A number a chart may plot, or null when the value is not a measurement. */
export function plottableValue(result) {
  if (!result) return null
  const kind = result.value_kind || 'numeric'
  if (kind === 'qualitative' || kind === 'unreadable') return null
  if (result.value === null || result.value === undefined) return null
  return Number(result.value)
}

export function formatMarkerValue(result, t) {
  if (!result) return '—'
  const kind = result.value_kind || 'numeric'
  if (kind === 'unreadable') return t('bloodTests.context.value.unreadable')
  if (kind === 'qualitative') return result.value_text || '—'
  if (kind === 'censored') {
    const key = OPERATOR_KEYS[result.value_operator]
    if (key && result.value !== null && result.value !== undefined) {
      return t(key, { value: result.value })
    }
    return result.value_text || t('bloodTests.context.value.unreadable')
  }
  if (result.value === null || result.value === undefined) return result.value_text || '—'
  return String(result.value)
}

/**
 * Trend chart points. A censored value sits at its limit and is marked, so
 * the line never drops to zero; qualitative and unreadable values are gaps.
 */
export function buildTrendPoints(history, t, formatDate = (d) => new Date(d).toLocaleDateString()) {
  return (history || [])
    .map((h) => ({
      date: formatDate(h.test_date),
      fullDate: h.test_date,
      value: plottableValue(h),
      censored: isCensored(h),
      operator: h.value_operator || null,
      label: formatMarkerValue(h, t),
    }))
    .sort((a, b) => new Date(a.fullDate) - new Date(b.fullDate))
}

/** Percent change between two points, or null when either is not a measurement. */
export function trendChangePercent(first, last) {
  if (!first || !last) return null
  if (first.censored || last.censored) return null
  if (first.value === null || last.value === null || first.value === 0) return null
  return ((last.value - first.value) / first.value) * 100
}
